import type { SimulationConfig } from './schema';

function mulberry32(seed: number): () => number {
  return function() {
    seed |= 0; seed = seed + 0x6D2B79F5 | 0;
    let t = Math.imul(seed ^ seed >>> 15, 1 | seed);
    t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}

export function computeCorners(
  config: SimulationConfig,
  tileCol = 0,
  tileRow = 0,
): [number[], number[], number[], number[]] {
  const col = (config as any)._tileCol ?? tileCol;
  const row = (config as any)._tileRow ?? tileRow;
  const ps = config.phaseSpace;
  if (ps.mode === 'tiling') {
    const t = ps.tiling;
    const get = (c: number, r: number): number[] => t.controlNet[r][c];
    return [get(col, row).slice(), get(col + 1, row).slice(), get(col, row + 1).slice(), get(col + 1, row + 1).slice()];
  }
  const iv: number[] = [];
  for (const d of ['angle1', 'velocity1', 'stretch1', 'stretchRate1', 'angle2', 'velocity2', 'stretch2', 'stretchRate2'] as const) {
    iv.push(ps.initialValues[d]);
  }
  const xDim = ps.x.dimension;
  const yDim = ps.y.dimension;
  const xIdx = ['angle1', 'velocity1', 'stretch1', 'stretchRate1', 'angle2', 'velocity2', 'stretch2', 'stretchRate2'].indexOf(xDim);
  const yIdx = ['angle1', 'velocity1', 'stretch1', 'stretchRate1', 'angle2', 'velocity2', 'stretch2', 'stretchRate2'].indexOf(yDim);
  const xb = new Array(8).fill(0);
  xb[xIdx] = 1;
  const yb = new Array(8).fill(0);
  yb[yIdx] = 1;
  const add = (base: number[], sx: number, sy: number): number[] =>
    base.map((v, i) => v + sx * xb[i] + sy * yb[i]);
  return [
    add(iv, ps.x.min, ps.y.min),
    add(iv, ps.x.max, ps.y.min),
    add(iv, ps.x.min, ps.y.max),
    add(iv, ps.x.max, ps.y.max),
  ];
}

export function bilinearSample(
  corners: [number[], number[], number[], number[]],
  u: number,
  v: number,
): number[] {
  const [c00, c10, c01, c11] = corners;
  return c00.map((_, i) =>
    (1 - u) * (1 - v) * c00[i] + u * (1 - v) * c10[i]
    + (1 - u) * v * c01[i] + u * v * c11[i],
  );
}

export function rigidPack(dir8: number[]): [number, number, number, number] {
  return [dir8[0], dir8[1], dir8[4], dir8[5]];
}

export function elasticPackA(dir8: number[]): [number, number, number, number] {
  return [dir8[0], dir8[1], dir8[2], dir8[3]];
}

export function elasticPackB(dir8: number[]): [number, number, number, number] {
  return [dir8[4], dir8[5], dir8[6], dir8[7]];
}

export function generateTiling(system: string, sculptureN: number, cols: number, rows: number, center: number[], seed = 0) {
  const availIdx = [0, 1, 4, 5, 2, 3, 6, 7];
  const scale = [Math.PI, 5, Math.PI, 5, 0.5, 5, 0.5, 5];
  const rand = mulberry32(seed);
  const net: number[][][] = [];
  for (let r = 0; r <= rows; r++) {
    const rowPts: number[][] = [];
    for (let c = 0; c <= cols; c++) {
      const pt = center.slice();
      for (const idx of availIdx) {
        pt[idx] = center[idx] + (rand() * 2 - 1) * scale[idx];
      }
      rowPts.push(pt);
    }
    net.push(rowPts);
  }
  const toroidal = cols >= 2 && rows >= 2;
  if (toroidal) {
    for (let r = 0; r <= rows; r++) {
      net[r][cols] = net[r][0].slice();
    }
    for (let c = 0; c <= cols; c++) {
      net[rows][c] = net[0][c].slice();
    }
  }
  return { cols, rows, toroidal, controlNet: net };
}

export function describeTiling(t: { cols: number; rows: number; toroidal: boolean }): string {
  return `${t.cols}x${t.rows}${t.toroidal ? ' torus' : ''}`;
}
